import { HttpClient, HttpParams } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    signal,
    untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PermissionRole } from 'app/core/enums/generated/PermissionRole';
import { ReconStatusEnum } from 'app/core/enums/generated/ReconStatusEnum';
import { ReconTypeEnum } from 'app/core/enums/generated/ReconTypeEnum';
import { WidgetTypeEnum } from 'app/core/enums/generated/WidgetTypeEnum';
import { SecurityService } from 'app/core/security/security.service';
import { sysInvReconciliationStatusStyle } from 'app/formulary-inventory/reconciliation/sys-inv-reconciliations/reconciliation-details/reconciliation-details.component';
import { CycleCountWidgetVm } from 'app/shared/generated/Administration/Models/Users/CycleCountWidgetVm';
import { UserJsVm } from 'app/shared/generated/Administration/Models/Users/UserJsVm';
import { UserWidgetVm } from 'app/shared/generated/Administration/Models/Users/UserWidgetVm';
import { LinkComponent } from 'app/shared/link/link.component';
import { EnumToDescriptionPipe } from 'app/shared/pipes/enumToDescription.pipe';
import { QuickUserInfoService } from 'app/shared/quick-user-info/quick-user-info.service';
import { SharedModule } from 'app/shared/shared.module';
import { BadgeModule } from 'primeng/badge';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ResizableWidgetComponent } from '../resizable-widget/resizable-widget.component';

@Component({
    selector: 'pcg-sys-inv-cycle-count-widget',
    imports: [
        SharedModule,
        EnumToDescriptionPipe,
        TooltipModule,
        BadgeModule,
        TagModule,
        LinkComponent,
        ResizableWidgetComponent
    ],
    templateUrl: './sys-inv-cycle-count-widget.component.html',
    styleUrl: './sys-inv-cycle-count-widget.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SysInvCycleCountWidgetComponent {
    // #region Injections
    readonly infoService = inject(QuickUserInfoService);
    private readonly http = inject(HttpClient);
    private readonly sec = inject(SecurityService);
    private readonly destroyRef = inject(DestroyRef);
    // #endregion

    // #region State

    /** The current user's profile. Populated on initialization. */
    readonly user = signal<UserJsVm | null>(null);
    /** Widget configuration loaded from the API, including module and role requirements. */
    readonly widgetVm = signal<UserWidgetVm | null>(null);
    /** The list of in-progress cycle counts for the selected facility. */
    readonly cycleCounts = signal<CycleCountWidgetVm[] | null>(null);
    /** True while any HTTP request is in flight. */
    readonly fetching = signal<boolean>(false);
    /** True if the current user has the required module access for this widget based on global admin configuration. */
    readonly canAccess = signal<boolean>(false);
    /** True if the user has access to the currently selected facility. */
    readonly canFacilityAccess = signal<boolean>(false);

    // #endregion

    // #region Computed

    /** Derives the business area module from the widget configuration. */
    readonly ba = computed(() => this.widgetVm()?.module);
    /** Derives the required permission roles from the widget configuration. */
    readonly roles = computed(() => this.widgetVm()?.widgetPermissionRoleIds?.map((id: PermissionRole) => id) ?? []);
    /** Shortcut to the currently selected facility's ID. */
    readonly facilityId = computed(() => this.infoService.userFacilityId());

    // #endregion

    // #region Enums (template-accessible)

    readonly statusEnum = ReconStatusEnum;
    readonly typeEnum = ReconTypeEnum;

    // #endregion

    // #region Private

    readonly hasBeenInitialized = signal<boolean>(false);

    /**
     * Subscribes to facility-change events so cycle counts and facility access
     * are refreshed whenever the user switches facilities.
     */
    readonly bindToFacilityChanges = effect(() => {
        this.infoService.facilityChangeNotifier();
        if (!this.infoService.isInitialized()) { return; }
        untracked(() => {
            if (this.hasBeenInitialized()) {
                this.getCycleCounts();
            }
        });
    });

    // #endregion

    // #region Lifecycle
    ngOnInit() { this.initialize(); }
    // #endregion

    // #region Initialization

    /** Seeds the user, fetches the facility, loads widget config, and subscribes to facility changes. */
    initialize(): void {
        this.user.set(this.sec.getUser());
        this.getWidgetDetails();
    }

    // #endregion

    // #region Data Fetching

    /**
     * Loads widget configuration from the API, evaluates module access,
     * then kicks off facility access and cycle count fetches.
     */
    getWidgetDetails(): void {
        this.fetching.set(true);
        const params = new HttpParams().set('type', WidgetTypeEnum.SysInvCycleCounts);
        this.http
            .get<UserWidgetVm>('api/Admin/Widgets/GetWidgetByType', { params })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (vm: UserWidgetVm) => {
                    this.widgetVm.set(vm ?? null);
                    this.canAccess.set(this.sec.hasModuleAccess([this.ba()], this.roles()));
                    this.getCycleCounts();
                    this.hasBeenInitialized.set(true);
                },
                error: (error) => {
                    console.error('Error fetching widget details:', error);
                    this.fetching.set(false);
                },
            });
    }

    /**
     * Fetches in-progress cycle counts for the selected facility.
     * No-ops if the user does not have module access.
     */
    getCycleCounts(): void {
        if (!this.canAccess()) {
            this.cycleCounts.set([]);
            this.fetching.set(false);
            return;
        }

        this.fetching.set(true);
        const params = new HttpParams().set('facilityId', this.facilityId());
        this.http
            .get<CycleCountWidgetVm[]>('api/Admin/Widgets/GetCycleCountWidget', { params })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (cc: CycleCountWidgetVm[]) => {
                    this.cycleCounts.set(cc ?? []);
                    this.fetching.set(false);
                },
                error: (error) => {
                    console.error('Error fetching cycle counts:', error);
                    this.cycleCounts.set([]);
                    this.fetching.set(false);
                },
            });
    }

    // #endregion

    // #region Navigation

    /** Navigates to the reconciliation detail page for the given cycle count row. */
    getReconUrl = (row: CycleCountWidgetVm): string => `inv/invs/reconciliations/${row.reconciliationId}/${row.inventoryId}`;

    // #endregion

    // #region UI Helpers

    /** Returns the PrimeIcons class for the given reconciliation status. */
    getStatusIcon(status: ReconStatusEnum): string {
        return sysInvReconciliationStatusStyle[status]?.icon;
    }

    /** Returns the Bootstrap text-color class for the given reconciliation status. */
    getStatusColor(status: ReconStatusEnum): string {
        return sysInvReconciliationStatusStyle[status]?.color;
    }
    // #endregion
}
